import { DownloadCategory, GameSchema } from '@/lib/types';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import CalendarBadgeIcon from '@/assets/calendar-badge.svg?react';
import VolatilityBadgeIcon from '@/assets/volatility-badge.svg?react';
import MinMaxBetBadgeIcon from '@/assets/minMaxBet-badge.svg?react';
import BuyFeatureBadgeIcon from '@/assets/buyFeature-badge.svg?react';
import StatusBadgeIcon from '@/assets/status-badge.svg?react';
import Button from '../Button/Button';
import DownloadIcon from '@/assets/download.svg?react';
import CheckIcon from '@/assets/check.svg?react';
import { useRef, useState, useMemo } from 'react';
import gameTilePlaceholder from '@/assets/game-tile-placeholder.png';
import Popper from '../Popper/Popper';
import GameTileCheckbox from './GameTileCheckbox';
import { useDownload } from '@/hooks/useDownload';
import Date from '@/components/Date/Date';
import { Volatilities } from '@/constants/Volatilities';
import { GameStatuses } from '@/constants/GameStatuses';
import GameRtpList from '../GameRtpList/GameRtpList';
import { useNavigate } from 'react-router';
import useHasUserPermissions from '@/hooks/useHasUserPermissions';
import { useGAMouseOver } from '@/hooks/useGAMouseOver';
import GameStudioLogo from './GameStudioLogo';
import Tooltip from '@/components/Tooltip/Tooltip';
import dayjs from 'dayjs';
import { ga } from '@/lib/analytics';

export const GameTileWidth = 280;

const GameCard = ({
  games,
  selected,
  onSelect,
  showDownload = true,
  onDetailsNavigate,
  section,
  defaultGame: defaultGameProp,
  showReleaseDateIcon = false,
}: {
  games: GameSchema[];
  selected?: boolean;
  onSelect?: () => void;
  showDownload?: boolean;
  onDetailsNavigate?: () => void;
  section: string;
  defaultGame: GameSchema;
  showReleaseDateIcon?: boolean;
}) => {
  const navigate = useNavigate();
  const downloadRef = useRef<HTMLDivElement>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { download, loading } = useDownload();
  const { handleMouseEnter: handleMouseEnterGA, handleMouseLeave } =
    useGAMouseOver();

  const defaultGame = useMemo(() => {
    return defaultGameProp || games[0];
  }, [defaultGameProp, games]);

  const [selectedRtp, setSelectedRtp] = useState<number | undefined>(
    defaultGame.rtp,
  );

  // The pre-selected variant changes when the list is re-ranked (e.g. sorting
  // by RTP min -> max vs max -> min), so follow the default instead of
  // holding on to the variant selected for the previous ranking.
  const [prevDefaultRtp, setPrevDefaultRtp] = useState<
    number | undefined
  >(defaultGame.rtp);
  if (prevDefaultRtp !== defaultGame.rtp) {
    setPrevDefaultRtp(defaultGame.rtp);
    setSelectedRtp(defaultGame.rtp);
  }

  const selectedGame = useMemo(() => {
    if (selectedRtp !== null) {
      const gameWithRtp = games.find((g) => g.rtp === selectedRtp);
      if (gameWithRtp) {
        return gameWithRtp;
      }
    }
    return defaultGame;
  }, [games, selectedRtp, defaultGame]);

  const hasRtps = useMemo(() => {
    const uniqueRtps = Array.from(
      new Set(
        games.map((game) => game.rtp).filter((rtp) => rtp != null),
      ),
    );
    return uniqueRtps.length > 0;
  }, [games]);

  const {
    studio_game_id,
    game_name,
    game_group,
    release_date,
    volatility,
    min_bet,
    max_bet,
    buy_feature,
    status,
    photo_url,
    id,
    game_provider,
    downloadable_files,
  } = selectedGame;

  const displayName = game_group || game_name;
  const releaseDateDayJs = dayjs(release_date);
  const releaseDateMonth = releaseDateDayJs.format('MMM');
  const releaseDateDay = releaseDateDayJs.format('DD');
  const complianceDisabled =
    !downloadable_files?.has_compliance_documents;
  const marketingDisabled = !downloadable_files?.has_marketing_assets;
  const canDownloadComplianceDocuments = useHasUserPermissions([
    'compliance_documents.download',
  ]);
  const canDownloadMarketingAssets = useHasUserPermissions([
    'marketing_assets.download',
  ]);
  const canDownloadGameSheets = useHasUserPermissions([
    'game_sheets.download',
  ]);
  const canDownloadMoreThanOne =
    [
      canDownloadComplianceDocuments,
      canDownloadMarketingAssets,
      canDownloadGameSheets,
    ].filter(Boolean).length > 1;

  const handleRtpSelect = (rtp: number) => {
    ga.trackMouseClick({
      section,
      component: 'game_tile_rtp_select',
      rtp,
      game_id: studio_game_id,
      game_name: game_name,
      status,
    });
    setSelectedRtp(rtp);
  };

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloadOpen(true);
  };

  const handleDetailsClick = (e: React.MouseEvent) => {
    ga.trackMouseClick({
      section,
      component: 'game_tile_details',
      game_id: studio_game_id,
      game_name: game_name,
      status,
    });
    e.stopPropagation();
    onDetailsNavigate?.();
    navigate(`/game/${studio_game_id}`);
  };

  const handleMouseEnter = () => {
    handleMouseEnterGA({
      section,
      component: 'game_tile',
      gameId: studio_game_id,
      gameName: game_name,
      status,
    });
    setIsHovered(true);
  };

  const handleMouseLeaveCard = () => {
    handleMouseLeave();
    setIsHovered(false);
  };

  const handleDownload = (
    category: DownloadCategory,
    suffix?: string,
  ) => {
    const entityName = game_name + (suffix ? `_${suffix}` : '');

    ga.trackMouseClick({
      section,
      component: 'game_tile_download',
      game_id: studio_game_id,
      game_name: game_name,
      status,
    });
    download({
      category,
      game_ids: [id],
      entity_name: entityName,
      game_name,
    });
    setDownloadOpen(false);
  };

  const handleDownloadGameSheets = () =>
    handleDownload('game_sheets', 'game_sheets');

  const handleDownloadComplianceDocuments = () =>
    handleDownload('compliance_documents', 'compliance');

  const handleDownloadMarketingAssets = () =>
    handleDownload('marketing_assets', 'marketing');

  const handleDownloadAll = () => handleDownload('all');

  return (
    <Root
      className={downloadOpen ? 'active' : ''}
      role="article"
      aria-label={`Game: ${displayName}`}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeaveCard}
    >
      {photo_url ? (
        <Bg
          src={photo_url}
          aria-label={`${displayName} background`}
        />
      ) : (
        <PlaceholderImage
          src={gameTilePlaceholder}
          alt="No image available"
          aria-label="No image available"
        />
      )}
      {status === 'coming_soon' && (
        <ComingSoonRibbon
          className="coming-soon-ribbon"
          $unknownReleaseDate={!release_date}
        >
          <FormattedMessage
            id="comingSoon"
            defaultMessage="Coming Soon"
          />
        </ComingSoonRibbon>
      )}
      {showReleaseDateIcon && (
        <ReleaseDateIcon>
          <ReleaseDateMonth>{releaseDateMonth}</ReleaseDateMonth>
          <ReleaseDateDay>{releaseDateDay}</ReleaseDateDay>
        </ReleaseDateIcon>
      )}
      <Overlay className="overlay" aria-label="game attributes">
        <Header $isHovered={isHovered}>
          <GameIdRow>
            {game_provider && (
              <GameStudioLogo gameProvider={game_provider} />
            )}
            <GameId aria-label="Studio Game ID">
              {studio_game_id}
            </GameId>
          </GameIdRow>
          <Tooltip title={displayName}>
            <GameName
              aria-label="Game group"
              data-testid="game-title"
            >
              {displayName}
            </GameName>
          </Tooltip>
        </Header>
        <Rows>
          {hasRtps && (
            <>
              <RtpRow aria-label="RTP options">
                <Value>
                  <FormattedMessage
                    id="rtpOptions"
                    defaultMessage="RTP options"
                  />
                  {': '}
                </Value>
              </RtpRow>
              <RtpListRow>
                <GameRtpList
                  games={games}
                  onRtpSelect={handleRtpSelect}
                  currentRtp={selectedRtp}
                />
              </RtpListRow>
            </>
          )}
          <Row aria-label="Release date">
            <CalendarBadgeIcon />
            <Value>
              <FormattedMessage
                id="releaseDate"
                defaultMessage="Release Date"
              />
              {': '}
              <b>
                <Date value={release_date} />
              </b>
            </Value>
          </Row>
          <Row aria-label="Volatility">
            <VolatilityBadgeIcon />
            <Value>
              <FormattedMessage
                id="volatility"
                defaultMessage="Volatility"
              />
              {': '}
              <b>
                {
                  Volatilities.find(
                    ({ value }) => value[0] === Number(volatility),
                  )?.name
                }
              </b>
            </Value>
          </Row>
          <Row aria-label="Min Max Bet">
            <MinMaxBetBadgeIcon />
            <Value>
              <FormattedMessage
                id="minMaxBet"
                defaultMessage="Min | Max Bet"
              />
              {': '}
              <b>
                {min_bet}
                {' | '}
                {max_bet}
              </b>
            </Value>
          </Row>
          <Row aria-label="Buy Feature">
            <BuyFeatureBadgeIcon />
            <Value>
              <FormattedMessage
                id="buyFeature"
                defaultMessage="Buy Feature"
              />
              {': '}
              <b>{buy_feature ? 'Supported' : 'Not Supported'}</b>
            </Value>
          </Row>
          <Row aria-label="Status">
            <StatusBadgeIcon />
            <Value>
              <FormattedMessage id="status" defaultMessage="Status" />
              {': '}
              <b>
                {GameStatuses[
                  status?.toLowerCase() as keyof typeof GameStatuses
                ]?.label || status}
              </b>
            </Value>
          </Row>
        </Rows>
        <ButtonRow>
          {showDownload && (
            <DownloadButton
              variant="contained"
              onClick={handleDownloadClick}
              aria-label={`Download options for ${game_name}`}
              loading={loading}
            >
              <DownloadIcon />
              <FormattedMessage
                id="download"
                defaultMessage="Download"
              />
            </DownloadButton>
          )}
          <Button
            variant="contained"
            onClick={handleDetailsClick}
            aria-label={`View details for ${game_name}`}
          >
            <FormattedMessage id="details" defaultMessage="Details" />
          </Button>
        </ButtonRow>
        <StyledPopper
          anchorEl={downloadRef.current}
          open={downloadOpen}
          onClose={() => setDownloadOpen(false)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Download options popper"
        >
          <Item
            onClick={handleDownloadGameSheets}
            aria-label="Download game sheets"
          >
            <div>
              <FormattedMessage
                id="gameSheets"
                defaultMessage="Game Sheets"
              />
            </div>
            <DownloadIcon />
          </Item>
          {canDownloadComplianceDocuments && (
            <Item
              onClick={
                complianceDisabled
                  ? undefined
                  : handleDownloadComplianceDocuments
              }
              aria-label="Download compliance documents"
              aria-disabled={complianceDisabled}
              $disabled={complianceDisabled}
            >
              <div>
                <FormattedMessage
                  id="complianceDocuments"
                  defaultMessage="Compliance Documents"
                />
              </div>
              <DownloadIcon />
            </Item>
          )}
          {canDownloadMarketingAssets && (
            <Item
              onClick={
                marketingDisabled
                  ? undefined
                  : handleDownloadMarketingAssets
              }
              aria-label="Download marketing assets"
              aria-disabled={marketingDisabled}
              $disabled={marketingDisabled}
            >
              <div>
                <FormattedMessage
                  id="marketingAssets"
                  defaultMessage="Marketing Assets"
                />
              </div>
              <DownloadIcon />
            </Item>
          )}
          {canDownloadMoreThanOne && (
            <Item
              onClick={handleDownloadAll}
              aria-label="Download all game assets"
            >
              <div>
                <FormattedMessage
                  id="downloadAll"
                  defaultMessage="Download All"
                />
              </div>
              <DownloadIcon />
            </Item>
          )}
        </StyledPopper>
        {onSelect && (
          <CheckboxWrapper className="checkbox-wrapper">
            <GameTileCheckbox
              checked={!!selected}
              onChange={onSelect}
              aria-label={`Select ${game_name}`}
            />
          </CheckboxWrapper>
        )}
      </Overlay>
      <PopperPosition ref={downloadRef} />
      {selected ? (
        <SelectedIcon className="selected-icon">
          <CheckIcon />
          <p>
            <FormattedMessage
              id="selected"
              defaultMessage="Selected"
            />
          </p>
        </SelectedIcon>
      ) : null}
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.radii.xs,
  width: GameTileWidth,
  height: 400,
  overflow: 'hidden',
  userSelect: 'none',
  boxShadow:
    '0px 10px 32px -4px #1319271A, 0px 6px 14px -6px #1319271F',

  '&.active, &:hover': {
    '& > .overlay': {
      top: 0,
    },

    '& .checkbox-wrapper': {
      opacity: 1,
    },

    '& .selected-icon': {
      opacity: 0,
    },

    '& .coming-soon-ribbon': {
      opacity: 0,
    },
  },
}));

const Bg = styled('div')<{ src: string }>(({ src }) => ({
  backgroundImage: `url(${src})`,
  backgroundSize: 'cover',
  backgroundPosition: 'top',
  width: '100%',
  height: '100%',
}));

const PlaceholderImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const Overlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 320,
  left: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  boxShadow:
    '0px 6px 14px -6px rgba(19, 25, 39, 0.12), 0px 10px 32px -4px rgba(19, 25, 39, 0.10)',
  background:
    'linear-gradient(106deg, rgba(19, 25, 39, 0.80) 19.92%, rgba(29, 38, 60, 0.80) 78.48%)',
  transition: '0.2s all ease-in-out',
}));

const Header = styled('div')<{ $isHovered: boolean }>(
  ({ $isHovered }) => ({
    paddingRight: $isHovered ? 46 : 0,
  }),
);

const GameIdRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 2,
  flexWrap: 'wrap',
});

const GameId = styled('div')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  color: theme.palette.textPrimaryWhite,
  opacity: 0.7,
  flexGrow: 0,
}));

const GameName = styled('div')(({ theme }) => ({
  fontSize: 15,
  fontWeight: 400,
  lineHeight: '24px',
  color: theme.palette.textPrimaryWhite,
  marginBottom: 22,
  flexGrow: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const Rows = styled('div')({
  flexGrow: 1,
});

const Row = styled('div')({
  display: 'flex',
  gap: 13,
  alignItems: 'center',
  marginBottom: 8,
});

const RtpRow = styled('div')({
  display: 'flex',
  gap: 13,
  alignItems: 'center',
  marginBottom: 8,
});

const RtpListRow = styled('div')({
  marginBottom: 12,
  marginLeft: 0,
});

const Value = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
  color: theme.palette.textPrimaryWhite,
  '& b': {
    fontWeight: 500,
  },
}));

const ButtonRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  width: '100%',
  '& > button': {
    flex: 1,
  },
}));

const DownloadButton = styled(Button)({
  '& .MuiCircularProgress-root': {
    color: '#fff',
  },
});

const PopperPosition = styled('div')({
  position: 'absolute',
  width: GameTileWidth - 32,
  height: 0,
  background: 'white',
  left: 16,
  bottom: 12,
});

const StyledPopper = styled(Popper)(({ theme }) => ({
  width: GameTileWidth - 32,
  background: theme.palette.white,
  borderRadius: theme.radii.xs,
  boxShadow:
    '0px 6px 14px -6px rgba(19, 25, 39, 0.12), 0px 10px 32px -4px rgba(19, 25, 39, 0.10)',
  padding: `${theme.spacing(1)} 0`,
}));

const Item = styled('div')<{ $disabled?: boolean }>(
  ({ theme, $disabled }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 12px',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    cursor: $disabled ? 'not-allowed' : 'pointer',
    color: theme.palette.textPrimaryBlack,
    opacity: $disabled ? 0.45 : 1,
    pointerEvents: $disabled ? 'none' : 'auto',

    path: {
      stroke: theme.palette.textPrimaryBlack,
    },

    '&:hover': {
      ...($disabled
        ? {}
        : {
            background: theme.palette.red50,
            color: theme.palette.red600,

            path: {
              stroke: theme.palette.red600,
            },
          }),
    },
  }),
);

const CheckboxWrapper = styled('div')({
  position: 'absolute',
  top: 26,
  right: 25,
  opacity: 0,
  transition: '0.2s all ease-in-out',
});

const SelectedIcon = styled('div')(({ theme }) => ({
  width: 50,
  height: 50,
  background: '#000000B2',
  borderRadius: theme.radii.xxs,
  position: 'absolute',
  top: 16,
  right: 16,
  border: `1px solid ${theme.palette.textPrimaryWhite}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.textPrimaryWhite,
  fontSize: 10,
  fontWeight: 500,
  lineHeight: '14px',
  gap: 4,
  opacity: 1,
  transition: '0.2s all ease-in-out',
  pointerEvents: 'none',

  svg: {
    width: 16,
    height: 16,

    path: {
      stroke: theme.palette.textPrimaryWhite,
    },
  },

  p: {
    margin: 0,
  },
}));

const ComingSoonRibbon = styled('div')<{
  $unknownReleaseDate?: boolean;
}>(({ theme, $unknownReleaseDate }) => ({
  position: 'absolute',
  top: 20,
  left: -40,
  width: 155,
  height: 36,
  background: $unknownReleaseDate
    ? 'rgba(0, 0, 0, 0.7)'
    : theme.palette.green700,
  border: '1px solid #ffffff99',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.textPrimaryWhite,
  fontSize: 12,
  fontWeight: 900,
  lineHeight: '16px',
  textTransform: 'uppercase',
  transform: 'rotate(-45deg)',
  zIndex: 10,
  opacity: 1,
  transition: 'opacity 0.2s ease-in-out',
}));

const ReleaseDateIcon = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 6,
  right: 10,
  width: 65,
  height: 63,
  background: '#000000B2',
  borderRadius: theme.radii.xxs,
  border: `1px solid #FFFFFFB2`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.textPrimaryWhite,
}));

const ReleaseDateMonth = styled('div')(() => ({
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '20px',
}));

const ReleaseDateDay = styled('div')(() => ({
  fontSize: 32,
  fontWeight: 400,
  lineHeight: '44px',
}));

export default GameCard;
